import { makeObservable, observable, action, reaction } from 'mobx';
import { Device } from '../services/DeviceRegistry';
import { ApprovalChallengeStatus } from '@wix/ambassador-identity-v1-verification/types';
import { PostLoginStore } from './postLogin';
import {
  ownerApp2FaSetupClickOnNextAfterChoosingDevice,
  ownerApp2FaSetupClickOnResendRequest,
} from '@wix/bi-logger-identity-data/v2';
import {
  EXPERIMENTS,
  RESEND_INDICATION_TTL,
  RESEND_INDICATION_TTL_TIMER,
} from './constants';
import { throttle } from 'lodash';
import { waitsFor } from './utils';

export enum Steps {
  DeviceSelection,
  Confirmation,
  Successful,
}

export class OwnerAppStore {
  public devices: Device[] = [];
  public selectedDevice: Device;
  public authStatus: ApprovalChallengeStatus =
    ApprovalChallengeStatus.PENDING_APPROVAL;
  public activeStep: Steps = Steps.DeviceSelection;
  public resendErrorMessage: string;
  private verificationId: string;
  private timer: NodeJS.Timer;
  private origin: string;
  public resendSuccessfullyIndication: boolean = false;
  public resendThrottleTimerValue: number = RESEND_INDICATION_TTL_TIMER / 1000;
  public shouldShowResendTimer: boolean = true;

  constructor(private postLoginStore: PostLoginStore) {
    makeObservable(this, {
      devices: observable,
      selectedDevice: observable,
      authStatus: observable,
      activeStep: observable,
      resendErrorMessage: observable,
      resendSuccessfullyIndication: observable,
      resendThrottleTimerValue: observable,
      onSelectDevice: action,
      enable: action.bound,
      clear: action.bound,
      sendPushToSelectedDevice: action.bound,
      nextStep: action.bound,
    });
    this.shouldShowResendTimer = postLoginStore.experiments.enabled(
      EXPERIMENTS.SHOULD_SHOW_RESEND_TIMER,
    );
    this.reactToResend();
    this.reactToAuthStatus();
    this.init();
  }

  public async init() {
    this.devices = await this.fetchUserDevices();
    this.selectedDevice = this.devices[0];
  }

  private reactToAuthStatus() {
    reaction(
      () => this.authStatus,
      async () => {
        if (this.authStatus !== ApprovalChallengeStatus.APPROVED) {
          return;
        }
        try {
          await this.enable();
          this.activeStep = Steps.Successful;
          return;
        } catch (e) {
          this.postLoginStore.notificationStore.open({
            content: this.postLoginStore.i18n.t(
              'post.login.oa.2fa.error.general',
            ),
            theme: 'error',
          });
        }
      },
    );
  }

  public async enable() {
    await this.postLoginStore.accountSettingsApi.OwnerApp.enable(
      this.verificationId,
      this.selectedDevice?.id,
    );
    this.clear();
  }

  public clear() {
    this.selectedDevice = this.devices?.[0];
    this.authStatus = ApprovalChallengeStatus.PENDING_APPROVAL;
    this.activeStep = Steps.DeviceSelection;
  }

  public fetchUserDevices = async () =>
    this.postLoginStore.deviceRegistry.fetchUserDevices(
      this.postLoginStore.userDetails.guid
    );

  onSelectDevice(id: string | number) {
    this.selectedDevice = this.devices.find((device) => device.id === id)!;
  }

  async sendPushToSelectedDevice(resend?: boolean) {
    try {
      if (this.timer) {
        clearInterval(this.timer);
      }
      if (resend) {
        this.resendSuccessfullyIndication = true;
        this.postLoginStore.biLogger.report(
          ownerApp2FaSetupClickOnResendRequest({
            origin: this.origin,
          }),
        );
      }
      const res =
        await this.postLoginStore.accountSettingsApi.sendVerificationCode({
          deviceId: this.selectedDevice?.id,
        });
      this.verificationId = res.data.verificationId;
      this.checkOwnerAuthStatus(this.verificationId);
    } catch (e) {
      if (resend) {
        return (this.resendErrorMessage = this.postLoginStore.i18n.t(
          'post.login.oa.2fa.error.resend',
        ));
      }
      this.postLoginStore.notificationStore.open({
        content: this.postLoginStore.i18n.t('post.login.oa.2fa.error.general'),
        theme: 'error',
      });
    }
  }

  async checkOwnerAuthStatus(verificationId: string) {
    const TIME_OUT_EXPIRED = 60000;
    const interval = 2000;
    this.authStatus = ApprovalChallengeStatus.PENDING_APPROVAL;

    this.timer = setInterval(async () => {
      const result =
        await this.postLoginStore.accountSettingsApi.OwnerApp.getStatus(
          verificationId,
        );
      const status = result.challenge?.approvalChallenge?.status;
      this.authStatus = status ?? ApprovalChallengeStatus.PENDING_APPROVAL;
      if (status !== ApprovalChallengeStatus.PENDING_APPROVAL) {
        clearInterval(this.timer);
      }
    }, interval);

    setTimeout(() => {
      clearInterval(this.timer);
    }, TIME_OUT_EXPIRED);
  }

  async nextStep() {
    switch (this.activeStep) {
      case Steps.DeviceSelection:
        await this.sendPushToSelectedDevice();
        this.postLoginStore.biLogger.report(
          ownerApp2FaSetupClickOnNextAfterChoosingDevice({
            origin: this.origin,
          }),
        );
        this.activeStep = Steps.Confirmation;
        break;
      case Steps.Confirmation:
        this.activeStep = Steps.DeviceSelection;
        break;
    }
  }

  async prevStep() {
    switch (this.activeStep) {
      case Steps.Confirmation:
        this.postLoginStore.confirmationModalStore.open({
          title: this.postLoginStore.i18n.t(
            'post.login.oa.2fa.confirm.exit.title',
          ),
          content: this.postLoginStore.i18n.t(
            'post.login.oa.2fa.confirm.exit.message',
          ),
          okTitle: this.postLoginStore.i18n.t('post.login.oa.2fa.cancel.setup'),
          onOk: (close) => {
            this.postLoginStore.procceedToPostAuthUrl();
            close();
          },
          cancelTitle: this.postLoginStore.i18n.t('post.login.back'),
          newStyleTheme: 'standard',
        });
        break;
      default:
        this.postLoginStore.procceedToPostAuthUrl();
    }
  }

  stopPolling() {
    clearInterval(this.timer);
  }

  get primaryButtonText() {
    switch (this.activeStep) {
      case Steps.DeviceSelection:
        return this.postLoginStore.i18n.t('post.login.oa.2fa.primary');
      case Steps.Confirmation:
        return this.postLoginStore.i18n.t('post.login.oa.2fa.confirm.primary');
      default:
        return undefined;
    }
  }

  get secondaryButtonText() {
    switch (this.activeStep) {
      case Steps.DeviceSelection:
        return this.postLoginStore.i18n.t('post.login.oa.2fa.secondary');
      case Steps.Confirmation:
        return this.postLoginStore.i18n.t(
          'post.login.oa.2fa.confirm.secondary',
        );
      default:
        return undefined;
    }
  }

  get hasConnectedDevices() {
    return this.devices.length > 0;
  }

  private setResendThrottleTimer = throttle(async () => {
    const ticks = new Array(RESEND_INDICATION_TTL_TIMER / 1000).fill('');
    Promise.all(
      ticks.map(async (_, i) => {
        await waitsFor(i * 1000);
        this.resendThrottleTimerValue = ticks.length - i;
        if (ticks.length === i + 1) {
          await waitsFor(1000);
          this.resendSuccessfullyIndication = false;
        }
      }),
    );
  }, RESEND_INDICATION_TTL_TIMER);

  private reactToResend() {
    const resendTimeIndication = this.shouldShowResendTimer
      ? RESEND_INDICATION_TTL_TIMER
      : RESEND_INDICATION_TTL;
    reaction(
      () => this.resendSuccessfullyIndication,
      () => {
        if (!this.resendSuccessfullyIndication) {
          return;
        }
        if (this.shouldShowResendTimer) {
          return this.setResendThrottleTimer();
        }
        setTimeout(
          () => (this.resendSuccessfullyIndication = false),
          resendTimeIndication,
        );
      },
    );
  }
}
